import { Auth } from 'aws-amplify';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { useBoolean, useEffectOnce, useSearchParam } from 'react-use';
import { captureException } from '@sentry/react';
import { Loading } from 'components/AppLoader/Loading';
import { FlashMessage as FlashComponent } from 'components/FlashMessages';
import { NewPasswordFormData } from 'domains/auth/ForceNewPassword/NewPasswordForm';
import { NewPasswordForm } from 'domains/auth/ForceNewPassword/NewPasswordForm/form';
import { FlashMessage, useFlashMessages } from 'domains/flash-messages';
import { CognitoUser } from 'domains/auth/AWSCognitoWrapper/types';
import { Heading, PageContainer, Paragraph } from '@expressable/syntax-ds';
import { useCognitoUserContext } from '../AWSCognitoWrapper/context';
import { routes } from 'router/routes';

export const InviteLinkLogin = (): JSX.Element => {
  const [loading, setLoading] = useBoolean(false);
  const [initialLoading, setInitialLoading] = useBoolean(true);
  const { showFlash, clearFlash } = useFlashMessages();

  const { isLoggedIn, onRefreshSession } = useCognitoUserContext();

  const [user, setUser] = useState<CognitoUser | null>(null);
  const { t } = useTranslation();

  const navigate = useNavigate();

  const emailParam = useSearchParam('email');
  const codeParam = useSearchParam('code');
  const selfSignUpParam = useSearchParam('selfsignup');

  const translationSuffix = selfSignUpParam === 'true' ? '.fromSignUp' : '';

  const onSubmitNewPassword = ({ password }: NewPasswordFormData) => {
    clearFlash();
    setLoading(true);
    Auth.completeNewPassword(user, password)
      .then(() => {
        onRefreshSession();
      })
      .catch(() => {
        setLoading(false);
        showFlash([
          {
            variant: 'error',
            message: t('form.validation.unknownError'),
            key: 'form-feedback',
          },
        ]);
      });
  };

  const onLinkError = (error?: Error) => {
    captureException(new Error(t('login.invitation.invalidToken')), {
      extra: {
        errorMessage: error?.message ?? '',
      },
    });

    navigate(routes.root, {
      state: {
        flash: [
          {
            variant: 'error',
            message: t('login.invitation.invalidToken'),
            key: 'form-feedback',
          },
        ] as FlashMessage[],
      },
    });
  };

  // Login with the incomplete user and make sure its in the correct state. If not, direct to login (onLinkError)
  const login = async () => {
    if (!emailParam || !codeParam) {
      onLinkError();
      return;
    }

    Auth.signIn(emailParam, codeParam)
      .then(u => {
        if (u.challengeName === 'NEW_PASSWORD_REQUIRED') {
          // Signing in with an incomplete user won't clear any previous sessions so if there's an active session we clear it here
          Auth.signOut();
          setUser(u as CognitoUser);
        } else {
          throw new Error('user registration is already complete');
        }
      })
      .catch(error => {
        onLinkError(error);
      })
      .finally(() => setInitialLoading(false));
  };

  useEffectOnce(() => {
    if (!isLoggedIn) {
      login();
    } else {
      navigate(routes.root);
    }
  });

  if (isLoggedIn) {
    return <Navigate to={routes.root} />;
  }

  if (initialLoading) {
    return <Loading className="transition-all duration-100 z-10" />;
  }

  return (
    <>
      <PageContainer variant="small-form" className="mt-[0px] mb-[0px]">
        <div className="text-center max-w-[600px] mx-auto">
          <Heading level="h1" className="mb-8 text-center">
            {t(`login.invitation.pageHeading${translationSuffix}`)}
          </Heading>

          <Paragraph size="lg" variant="primary">
            {t(`login.invitation.subTitle${translationSuffix}`)}
          </Paragraph>

          {selfSignUpParam === 'true' && (
            <div className="grow pt-40 pb-20 px-24 md:px-0 md:pt-2 max-w-[600px]">
              <FlashComponent variant="warning">{t('intake.adult.intakeCompletionWarning')}</FlashComponent>
            </div>
          )}
        </div>

        <NewPasswordForm
          selfSignUp={selfSignUpParam === 'true'}
          loading={loading}
          onSubmit={onSubmitNewPassword}
          emailParam={emailParam as string}
        />
      </PageContainer>
    </>
  );
};
